import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { EcgAnalysisSetTest } from "webportalclient";
import { DateMatchSortHeader } from "../Components/DateMatchSortHeader";
import { MatchSortHeader } from "../Components/MatchSortHeader";
import { useQuery } from "../Components/Query";
import { Column, Table } from "../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import { useSecurityContext } from "../Core/SecurityContext";
import { EcgAnalysisSetTypeEnum } from "../misc/api/ApiEcgAnalysisSetType";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { uniqBy } from "../misc/ArrayFunctions";
import { AssignToUserInGroupModal } from "../misc/components/AssignToUserInGroupModal";
import { Authorization } from "../misc/components/Authorization";
import Condition from "../misc/components/Condition";
import { formatDateTime } from "../misc/components/Formatters/Formatters";
import { HamburgerMenu } from "../misc/components/HamburgerMenu";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { CreateAnalysisBatchModal } from "../PulmonaryFunctionTests/QC/CreateAnalysisBatchModal";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { SortHeader } from "../Components/SortHeader";
import { SelectOption } from "./EcgAnalysisSetScreen";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import { SubjectTypes } from "../misc/api/apiTypes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

export const EcgAnalysisSetHamburgerMenu = (analysisSetTest: EcgAnalysisSetTest, onReassign: (analysisSetTestId: string, type: string) => void = undefined) => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions, currentUser } = useSecurityContext();

    return (
        <HamburgerMenu>
            <Authorization permissionId={PermissionEnum.AccessSingleECGSession}
                currentPermissions={currentUserPermissions}
                studyId={study.id}
            >
                <Condition test={() => analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.Overread || (analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC && analysisSetTest.analystId === currentUser.Id)}>
                    <li>
                        <Link to={`/ecg/analysis/${analysisSetTest.id}/false`}>View</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.OverreadECGSession}
                currentPermissions={currentUserPermissions}
                studyId={study.id}
            >
                <Condition test={() => analysisSetTest !== null
                    && (analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.Overread || analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.QC)
                    && (analysisSetTest.analystId === currentUser.Id || analysisSetTest.analyst === null)
                }>
                    <li>
                        <Link to={`/ecg/analysis/${analysisSetTest.id}/true`}>Analyse</Link>
                    </li>
                </Condition>
            </Authorization>
            <Authorization permissionId={PermissionEnum.ReviewECGSession}
                currentPermissions={currentUserPermissions}
                studyId={study.id}
            >
                <Condition test={() => analysisSetTest !== null
                    && analysisSetTest.analysisSet.type === EcgAnalysisSetTypeEnum.Review
                    && (analysisSetTest.analystId === currentUser.Id || analysisSetTest.analyst === null)
                }>
                    <li>
                        <Link to={`/ecg/analysis/${analysisSetTest.id}/false`}>Review</Link>
                    </li>
                </Condition>
            </Authorization>
            {onReassign ? <Authorization permissionId={PermissionEnum.OverreadECGSession} studyId={study.id} currentPermissions={currentUserPermissions}>
                <li>
                    <a onClick={() => onReassign(analysisSetTest.id, analysisSetTest.analysisSet.type)}>Reassign</a>
                </li>
            </Authorization> : null}
        </HamburgerMenu>
    );
}

export const formatAnalystSetType = (type: string) => {
    switch (type) {
        case EcgAnalysisSetTypeEnum.Overread:
            return "Overread";
        case EcgAnalysisSetTypeEnum.Review:
            return "Review";
        case EcgAnalysisSetTypeEnum.QC:
            return "QC Overread";
    }
}

export const formatAnalyst = analyst => analyst ? analyst : "-";

export const formatTestStatus = (status: boolean | null) => {
    return status === null ? <span className="label label-primary">Not Started</span> :
        (status ? <span className="label label-success">Completed</span> : <span className="label label-danger">Disagreement</span>)
}

export interface EcgAnalysisSetTableProps {
    analysts: SelectOption[];
    analysisSetTypes: SelectOption[];
    onSwitch: () => void;
}

export const EcgAnalysisSetTable = ({ analysts, analysisSetTypes, onSwitch }: EcgAnalysisSetTableProps) => {
    const { data, applyFilters, refresh } = useQuery();
    const location = useLocation();
    const { study } = useCurrentStudyConfigurationContext();
    const { overreadStatuses } = useReferenceContextProvider();
    const { currentUserPermissions, currentUser } = useSecurityContext();
    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [subjectTypeOptions, setSubjectTypeOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [analysisSetTestToReassign, setAnalysisSetTestToReassign] = React.useState<string>();
    const [isReassignOverreaderToUserModalVisible, setIsReassignOverreaderToUserModalVisible] = React.useState(false);
    const [isReassignReviewerToUserModalVisible, setIsReassignReviewerToUserModalVisible] = React.useState(false);
    const [qcSelectionModalVisible, setQCSelectionModelVisible] = React.useState(false);
    const [qcBatchSaved, setQcBatchSaved] = React.useState<boolean | undefined>(undefined);

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {
            const visits = await client.client.getEcgSessions("visit", `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($select=name)");
            const options = uniqBy(visits.value, (a, b) => a.visit.name === b.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getEcgSessions("name", `visit/subject/site/studyId eq ${study.id}`, "name");
            const options = uniqBy(sessions.value, (a, b) => a.name === b.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);

            const subjectTypesResult = await client.client.getSubjectTypes();
            const subjectTypes = subjectTypesResult.value.map(s => ({
                value: s.id,
                label: s.name,
            }));
            setSubjectTypeOptions(subjectTypes);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
    }, [client.client, study.id]);

    React.useEffect(() => {
        if (location.state?.refresh) {
            refresh();
        }
    }, [location.state?.refresh]);

    if (overreadStatuses.length === 0)
        return <LoadingWithMessage message="Loading ECG Recordings..." />

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const handleReassign = async (analystSetTestId: string, userId: string) => {
        await client.client.patchEcgAnalysisSetTest({
            analystId: userId
        }, analystSetTestId, "");
        setIsReassignReviewerToUserModalVisible(false);
        setIsReassignOverreaderToUserModalVisible(false);
        refresh();
    }

    const recordingStatusesOptions = [
        {
            value: null,
            label: "Not Started"
        },
        {
            value: true,
            label: "Completed"
        },
        {
            value: false,
            label: "Disagreement"
        },
    ];

    const reassign = (analysisSetId: string, type: string) => {
        setAnalysisSetTestToReassign(analysisSetId);

        if (type === EcgAnalysisSetTypeEnum.Review)
            setIsReassignReviewerToUserModalVisible(true);
        else
            setIsReassignOverreaderToUserModalVisible(true);
    }

    const formatSubjectType = (subjectTypeId: string) => {

        switch (subjectTypeId) {
            case SubjectTypes.InStudy:
                return "In-Study";
            case SubjectTypes.Training:
                return "Training";
            case SubjectTypes.Certification:
                return "Certification";
            default:
                return null;
        }
    }

    const handleQcBatchSavingAlertMessage = () => {
        if (qcBatchSaved) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-success">
                        <FontAwesomeIcon icon={faCheck} className="icon" />
                        QC batch is created successfully.
                    </div>
                </div>
            </div>);
        }
        else if (qcBatchSaved === false) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-danger">
                        <FontAwesomeIcon icon={faXmark} className="icon" />
                        There was an error. QC batch wasn't saved.
                    </div>
                </div>
            </div>);
        }
        else {
            return null
        }
    }

    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">ECG Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                        &nbsp;
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateAutomatedQcEcgSelection}>
                            <button className="btn btn-primary" onClick={() => setQCSelectionModelVisible(true)}>Create QC Batch</button>
                        </Authorization>
                        &nbsp;
                        <button className="btn btn-outline btn-primary" onClick={() => onSwitch()}>
                            Show QC Analysis Sets
                        </button>
                    </div>
                </div>
            </div>
            {handleQcBatchSavingAlertMessage()}
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive">
                                <Table data={data}>
                                    <Column field="analysisSet" header={<MatchSortHeader title="Site ID" field="analysisSet/session/visit/subject/site/id" options={siteOptions} sortField="analysisSet/session/visit/subject/site/number" />} formatter={analysisSet => analysisSet.session.visit.subject.site.number} />
                                    <Column field="analysisSet" header={<MatchSortHeader title="Subject ID" field="analysisSet/session/visit/subject/id" options={subjectOptions} sortField="analysisSet/session/visit/subject/number" />} formatter={analysisSet => analysisSet.session.visit.subject.number} />
                                    <Column field="analysisSet" header={<MatchSortHeader title="Subject Type" field="analysisSet/session/visit/subject/typeKey" options={subjectTypeOptions} sortField="analysisSet/session/visit/subject/typeKey" />} formatter={analysisSet => formatSubjectType(analysisSet.session.visit.subject.typeKey)} />
                                    <Column field="analysisSet" header={<MatchSortHeader title="Visit" field="analysisSet/session/visit/name" options={visitOptions} sortField="analysisSet/session/visit/name" />} formatter={analysisSet => analysisSet.session.visit.name} />
                                    <Column field="analysisSet" header={<MatchSortHeader title="Session" field="analysisSet/session/name" options={sessionOptions} sortField="analysisSet/session/name" />} formatter={analysisSet => analysisSet.session.name} />
                                    <Column field="analysisSet" header={<MatchSortHeader title="Analysis Set Type" field="analysisSet/type" options={analysisSetTypes} />} formatter={analysisSet => formatAnalystSetType(analysisSet.type)} />
                                    <Column field="test" header={<SortHeader title="Recording Number" field="test/index" />} formatter={test => test.index} />
                                    <Column field="test" header={<DateMatchSortHeader title="Recording Date Time" field="test/occurred" sortField="test/occurred" />} formatter={test => formatDateTime(test.occurred)} />
                                    <Column field="test" header={<DateMatchSortHeader title="Date Received" field="test/created" sortField="test/created" />} formatter={test => formatDateTime(test.created)} />
                                    <Column field="analysisSet" header={<DateMatchSortHeader title="Created At" field="analysisSet/created" sortField="analysisSet/created" />} formatter={analysisSet => formatDateTime(analysisSet.created)} />
                                    <Column field="analysisCompleted" header={<DateMatchSortHeader title="Last Analysed" field="analysisCompleted" sortField="analysisCompleted" />} formatter={analysisCompleted => formatDateTime(analysisCompleted)} />
                                    <Column field="accepted" header={<MatchSortHeader title="Status" field="accepted" options={recordingStatusesOptions} sortField="accepted" />} formatter={accepted => formatTestStatus(accepted)} />
                                    {ViewOverreaderName()
                                        ? <Column field="analyst" header={<MatchSortHeader title="Analyst" field="analyst" options={analysts} sortField="analyst" />} formatter={analyst => formatAnalyst(analyst)} />
                                        : null
                                    }
                                    <Column field="" formatter={a => EcgAnalysisSetHamburgerMenu(a, reassign)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AssignToUserInGroupModal show={isReassignOverreaderToUserModalVisible} headerText="Reassign Overread Analysis Set" permission={PermissionEnum.OverreadECGSession}
                onClose={() => setIsReassignOverreaderToUserModalVisible(false)}
                onAssign={(userId) => { handleReassign(analysisSetTestToReassign, userId) }}
            />
            <AssignToUserInGroupModal show={isReassignReviewerToUserModalVisible} headerText="Reassign Review Analysis Set" permission={PermissionEnum.ReviewECGSession}
                onClose={() => setIsReassignReviewerToUserModalVisible(false)}
                onAssign={(userId) => { handleReassign(analysisSetTestToReassign, userId) }}
            />
            {qcSelectionModalVisible ? <CreateAnalysisBatchModal currentUser={currentUser} sessionType={SessionTypes.Ecg} minimumTests={1} onSave={(result: boolean) => setQcBatchSaved(result)} onClose={() => setQCSelectionModelVisible(false)} /> : null}
        </>
    );
}
